import type { CSSProperties } from 'react';
import { useState, useEffect } from 'react';
import Header from '../components/Header';
import { Search } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { useData } from "../context/DataContext";
import { useCacheCheck } from '../hooks/useCacheCheck';
import { CacheStatus } from '../components/CacheStatus';
import { ExportButton } from '../components/ExportButton';

const axisTick = { fill: '#64748b', fontSize: 11 };
const axisLine = { stroke: '#e2e8f0' };
const gridStroke = '#cbd5e1';

const openRequestsTooltipContentStyle: CSSProperties = {
  backgroundColor: '#ffffff',
  border: '1px solid rgb(226 232 240)',
  borderRadius: '0.5rem',
  boxShadow: '0 4px 6px -1px rgb(15 23 42 / 0.08), 0 2px 4px -2px rgb(15 23 42 / 0.06)',
  fontSize: '12px',
  padding: '10px 12px',
};

const openRequestsTooltipLabelStyle: CSSProperties = {
  color: '#475569',
  fontWeight: 600,
  marginBottom: '4px',
};

interface GroupSeriesRequest {
  request_id: number;
  request_manager: string;
  venue: string;
  company: string;
  client_request_reference: string;
  checkin: string;
  checkout: string;
  estimated_revenue: number;
  nb_of_nights: number;
  total_number_of_rooms: number;
  request_date: string;
}

type SortConfig = {
  key: string;
  direction: 'asc' | 'desc';
};

export default function GroupSeriesReport() {
  const { requestsData, loading, error, refreshData } = useData();
  
  // Check cache when component mounts
  useCacheCheck();

  // Force fetch if requestsData is empty on mount
  useEffect(() => {
    if (!requestsData || requestsData.length === 0) {
      refreshData();
    }
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  const [data, setData] = useState<GroupSeriesRequest[]>([]);
  const [selectedCohort, setSelectedCohort] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);

  useEffect(() => {
    if (requestsData && requestsData.length > 0) {
      // Transform the requests data to match the expected format
      const groupSeriesData = requestsData.map((row: any) => ({
        request_id: row.request_id,
        request_manager: row.request_manager,
        venue: row.venue,
        company: row.company,
        client_request_reference: row.client_request_reference,
        checkin: row.checkin,
        checkout: row.checkout,
        estimated_revenue: row.estimated_revenue,
        nb_of_nights: row.nb_of_nights,
        total_number_of_rooms: row.total_number_of_rooms,
        request_date: row.request_date
      }));
      
      setData(groupSeriesData);
    }
  }, [requestsData]);

  // Helper function to calculate days between dates
  const calculateDaysSinceStartDate = (startDate: string | null): number => {
    if (!startDate) return 0;
    const start = new Date(startDate);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - start.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  // Helper to get cohort label for a row
  const getCohort = (row: GroupSeriesRequest) => {
    const days = calculateDaysSinceStartDate(row.request_date);
    if (days < 10) return '< 10 days';
    if (days < 30) return '< 30 days';
    if (days < 90) return '< 90 days';
    return '> 90 days';
  };

  // Sort data function
  const sortData = (data: GroupSeriesRequest[]) => {
    if (!sortConfig) return data;

    return [...data].sort((a, b) => {
      let aValue: any = a[sortConfig.key as keyof GroupSeriesRequest];
      let bValue: any = b[sortConfig.key as keyof GroupSeriesRequest];

      // Handle special cases
      if (sortConfig.key === 'request_date') {
        aValue = calculateDaysSinceStartDate(aValue);
        bValue = calculateDaysSinceStartDate(bValue);
      }

      // Handle null/undefined values
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      // Handle different data types
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }

      // Handle dates
      if (aValue instanceof Date && bValue instanceof Date) {
        return sortConfig.direction === 'asc' 
          ? aValue.getTime() - bValue.getTime() 
          : bValue.getTime() - aValue.getTime();
      }

      // Handle strings
      const aString = String(aValue).toLowerCase();
      const bString = String(bValue).toLowerCase();
      return sortConfig.direction === 'asc' 
        ? aString.localeCompare(bString) 
        : bString.localeCompare(aString);
    });
  };

  // Handle sort click
  const handleSort = (key: string) => {
    setSortConfig(current => ({
      key,
      direction: current?.key === key && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Filtered and sorted data for table
  const filteredData = data.filter(row => {
    // Cohort filter
    if (selectedCohort && getCohort(row) !== selectedCohort) {
      return false;
    }
    
    // Search filter
    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      const matchesSearch = 
        (row.request_manager && row.request_manager.toLowerCase().includes(lower)) ||
        (row.venue && row.venue.toLowerCase().includes(lower)) ||
        (row.company && row.company.toLowerCase().includes(lower)) ||
        (row.client_request_reference && row.client_request_reference.toLowerCase().includes(lower));
      if (!matchesSearch) return false;
    }
    
    return true;
  });

  const sortedData = sortData(filteredData);

  // Table data transformer for export
  const getTableDataForExport = () => {
    return sortedData.map(row => {
      const days = calculateDaysSinceStartDate(row.request_date);
      return {
        'Request ID': row.request_id,
        'Manager': row.request_manager || 'Unassigned',
        'Hotel': row.venue,
        'Company': row.company,
        'Ref Client': row.client_request_reference,
        'First Arrival Date': row.checkin,
        'Last Arrival': row.checkout,
        'Estimated Revenue': row.estimated_revenue,
        'Nights': row.nb_of_nights,
        'Total Rooms': row.total_number_of_rooms,
        'Start Date': row.request_date,
        'Days Since Start Date': days
      };
    });
  };

  // Compute buckets for the graph
  const bucketCounts = { '<10': 0, '<30': 0, '<90': 0, '>90': 0 };
  data.forEach(row => {
    const days = calculateDaysSinceStartDate(row.request_date);
    if (days < 10) bucketCounts['<10']++;
    else if (days < 30) bucketCounts['<30']++;
    else if (days < 90) bucketCounts['<90']++;
    else bucketCounts['>90']++;
  });
  const graphData = [
    { label: '< 10 days', value: bucketCounts['<10'] },
    { label: '< 30 days', value: bucketCounts['<30'] },
    { label: '< 90 days', value: bucketCounts['<90'] },
    { label: '> 90 days', value: bucketCounts['>90'] },
  ];

  return (
    <div className="min-h-screen bg-gray-100">
      <Header />
      <main className="max-w-[90rem] mx-auto py-6 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="page-title">Group Series Report - Open Requests</h1>
          <div className="flex items-center space-x-4">
            <ExportButton
              reportName="Group Series Open Requests"
              data={requestsData || []}
              filters={{
                selectedCreationYears: new Set(),
                selectedArrivalYears: new Set(),
                selectedManagers: new Set(),
                selectedSegments: new Set(),
                selectedClients: new Set(),
                selectedHotels: new Set(),
                selectedHotelGroups: new Set(),
                selectedArrivalMonths: new Set(),
                selectedArrivalQuarters: new Set(),
                selectedCreationMonths: new Set(),
                selectedCreationQuarters: new Set(),
                allClientsSelected: true,
                allHotelsSelected: true,
                allHotelGroupsSelected: true
              }}
              tableDataTransformer={getTableDataForExport}
            />
            <CacheStatus refreshData={refreshData} />
          </div>
        </div>
        
        {/* Graph section */}
        <section
          className="mb-6 overflow-hidden rounded-xl border border-slate-200/90 bg-white shadow-sm"
          aria-label="Requests by days since start date"
        >
          <div className="border-b border-slate-200/80 bg-slate-50/80 px-5 py-3.5">
            <h3 className="text-sm font-semibold tracking-tight text-slate-800">
              Requests by Days Since Start Date
            </h3>
          </div>
          <div className="px-3 pb-5 pt-4 sm:px-5">
            <div className="h-[300px] w-full cursor-pointer sm:h-[320px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={graphData}
                  layout="vertical"
                  margin={{ top: 8, right: 16, left: 8, bottom: 4 }}
                  barCategoryGap="12%"
                  onClick={state => {
                    if (state && state.activeLabel) {
                      setSelectedCohort(state.activeLabel === selectedCohort ? null : state.activeLabel);
                    }
                  }}
                >
                  <CartesianGrid stroke={gridStroke} strokeDasharray="3 3" horizontal vertical />
                  <XAxis
                    type="number"
                    allowDecimals={false}
                    tick={axisTick}
                    axisLine={axisLine}
                    tickLine={{ stroke: '#e2e8f0' }}
                  />
                  <YAxis
                    type="category"
                    dataKey="label"
                    tick={axisTick}
                    axisLine={axisLine}
                    tickLine={{ stroke: '#e2e8f0' }}
                    width={96}
                  />
                  <Tooltip
                    formatter={(value: number) => [value, 'Requests']}
                    labelFormatter={String}
                    labelStyle={openRequestsTooltipLabelStyle}
                    contentStyle={openRequestsTooltipContentStyle}
                    cursor={{ fill: 'rgb(24 141 130 / 0.06)' }}
                  />
                  <Bar dataKey="value" maxBarSize={36} radius={[0, 6, 6, 0]}>
                    {graphData.map(entry => (
                      <Cell
                        key={`cell-${entry.label}`}
                        fill={selectedCohort && entry.label === selectedCohort ? '#126f66' : '#188d82'}
                        style={{ cursor: 'pointer' }}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            {selectedCohort && (
              <div className="mt-3 text-center text-sm font-medium text-slate-600">
                Filtering: <span className="font-semibold text-slate-800">{selectedCohort}</span>
                <span className="text-slate-500"> — click the bar again to clear</span>
              </div>
            )}
          </div>
        </section>

        {/* Search field */}
        <div className="flex gap-4 mb-4 items-end justify-between">
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500 w-full"
            />
          </div>
        </div>

        {/* Table section */}
            {error ? (
              <div className="text-red-600">{error}</div>
            ) : loading ? (
              <div className="text-center">Loading...</div>
            ) : (
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th 
                      className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 align-top"
                      onClick={() => handleSort('request_id')}
                    >
                      <div className="flex items-start gap-1">
                        Request ID
                        {sortConfig?.key === 'request_id' && (
                          <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </div>
                    </th>
                                         <th 
                       className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 align-top"
                       onClick={() => handleSort('request_manager')}
                     >
                       <div className="flex items-start gap-1">
                         Manager
                         {sortConfig?.key === 'request_manager' && (
                           <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                         )}
                       </div>
                     </th>
                     <th 
                       className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 align-top"
                       onClick={() => handleSort('venue')}
                     >
                       <div className="flex items-start gap-1">
                         Hotel
                         {sortConfig?.key === 'venue' && (
                           <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                         )}
                       </div>
                     </th>
                     <th 
                       className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 align-top"
                       onClick={() => handleSort('company')}
                     >
                       <div className="flex items-start gap-1">
                         Company
                         {sortConfig?.key === 'company' && (
                           <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                         )}
                       </div>
                     </th>
                     <th 
                       className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 align-top"
                       onClick={() => handleSort('client_request_reference')}
                     >
                       <div className="flex items-start gap-1">
                         Ref Client
                         {sortConfig?.key === 'client_request_reference' && (
                           <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                         )}
                       </div>
                     </th>
                     <th 
                       className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 align-top"
                       onClick={() => handleSort('checkin')}
                     >
                       <div className="flex items-start gap-1">
                         First Arrival Date
                         {sortConfig?.key === 'checkin' && (
                           <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                         )}
                       </div>
                     </th>
                     <th 
                       className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 align-top"
                       onClick={() => handleSort('checkout')}
                     >
                       <div className="flex items-start gap-1">
                         Last Arrival
                         {sortConfig?.key === 'checkout' && (
                           <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                         )}
                       </div>
                     </th>
                     <th 
                       className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 align-top"
                       onClick={() => handleSort('estimated_revenue')}
                     >
                       <div className="flex items-start gap-1">
                         Estimated Revenue
                         {sortConfig?.key === 'estimated_revenue' && (
                           <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                         )}
                       </div>
                     </th>
                     <th 
                       className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 align-top"
                       onClick={() => handleSort('nb_of_nights')}
                     >
                       <div className="flex items-start gap-1">
                         Nights
                         {sortConfig?.key === 'nb_of_nights' && (
                           <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                         )}
                       </div>
                     </th>
                     <th 
                       className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 align-top"
                       onClick={() => handleSort('total_number_of_rooms')}
                     >
                       <div className="flex items-start gap-1">
                         Total rooms
                         {sortConfig?.key === 'total_number_of_rooms' && (
                           <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                         )}
                       </div>
                     </th>
                     <th 
                       className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 align-top"
                       onClick={() => handleSort('request_date')}
                     >
                       <div className="flex items-start gap-1">
                         Start date
                         {sortConfig?.key === 'request_date' && (
                           <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                         )}
                       </div>
                     </th>
                     <th 
                       className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 align-top"
                       onClick={() => handleSort('request_date')}
                     >
                       <div className="flex items-start gap-1">
                         Days since start date
                         {sortConfig?.key === 'request_date' && (
                           <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                         )}
                       </div>
                     </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {sortedData.map((row) => {
                    const fullName = row.request_manager || '';
                    const parts = fullName.split(' ');
                    const firstName = parts[0] || '';
                    const lastName = parts.slice(1).join(' ');
                    const days = calculateDaysSinceStartDate(row.request_date);
                    
                    return (
                      <tr key={row.request_id} className="hover:bg-gray-50">
                        <td className="px-2 py-2 whitespace-nowrap text-xs">{row.request_id}</td>
                        <td className="px-2 py-2 whitespace-nowrap text-xs">
                          <span className="flex flex-col font-semibold" title={fullName}>
                            <span>{firstName}</span>
                            <span>{lastName}</span>
                          </span>
                        </td>
                        <td className="px-2 py-2 whitespace-nowrap text-xs">
                          <span className="block max-w-[150px] whitespace-normal break-words" title={row.venue}>
                            {row.venue}
                          </span>
                        </td>
                        <td className="px-2 py-2 whitespace-nowrap text-xs">
                          <span className="block max-w-[180px] whitespace-normal break-words line-clamp-2" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }} title={row.company}>
                            {row.company}
                          </span>
                        </td>
                        <td className="px-2 py-2 whitespace-nowrap text-xs">
                          <span className="block max-w-[120px] whitespace-normal break-words line-clamp-2" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }} title={row.client_request_reference}>
                            {row.client_request_reference}
                          </span>
                        </td>
                        <td className="px-2 py-2 whitespace-nowrap text-xs">
                          {row.checkin ? new Date(row.checkin).toLocaleDateString() : '-'}
                        </td>
                        <td className="px-2 py-2 whitespace-nowrap text-xs">
                          {row.checkout ? new Date(row.checkout).toLocaleDateString() : '-'}
                        </td>
                        <td className="px-2 py-2 whitespace-nowrap text-xs">
                          {row.estimated_revenue != null ? row.estimated_revenue.toLocaleString(undefined, { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }) : '-'}
                        </td>
                        <td className="px-2 py-2 whitespace-nowrap text-xs">{row.nb_of_nights}</td>
                        <td className="px-2 py-2 whitespace-nowrap text-xs">{row.total_number_of_rooms}</td>
                        <td className="px-2 py-2 whitespace-nowrap text-xs">
                          {row.request_date ? new Date(row.request_date).toLocaleDateString() : '-'}
                        </td>
                        <td className="px-2 py-2 whitespace-nowrap text-xs">{days.toLocaleString()}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
} 